/* eslint-disable class-methods-use-this */
import { CustomFieldDefinition, Location, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { Requests, arrayBufferToImageSrcBase64 } from '@nx-smartmonkey/shared/helpers';

import { GeoFence } from '../../domain/geoFence/GeoFence';
import { GeoFenceDTO } from '../../domain/geoFence/GeoFenceDTO';
import { FlatProject } from '../../domain/project/FlatProject';
import { FlatProjectDTO } from '../../domain/project/FlatProjectDTO';
import { Project } from '../../domain/project/Project';
import { ProjectCustomerEmailTypes, ProjectDTO } from '../../domain/project/ProjectDTO';
import { ProjectUser } from '../../domain/project/ProjectUser';
import { ProjectUserDTO } from '../../domain/project/ProjectUserDTO';
import { ProjectUserRoles } from '../../domain/project/ProjectUserRoles';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { GeoFenceMapper } from '../../mappers/geoFence/GeoFenceMapper';
import { FlatProjectMapper } from '../../mappers/project/FlatProjectMapper';
import { ProjectCustomerEmailMapper } from '../../mappers/project/ProjectCustomerEmailMapper';
import { ProjectCustomerSMSMapper } from '../../mappers/project/ProjectCustomerSMSMapper';
import { ProjectMapper } from '../../mappers/project/ProjectMapper';
import { ProjectUserMapper } from '../../mappers/project/ProjectUserMapper';
import { VehicleMapper } from '../../mappers/vehicle/VehicleMapper';
import { SessionRepository } from '../session/SessionRepository';
import { ProjectRepository } from './ProjectRepository';

export class ProjectRepositoryApi implements ProjectRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async create({
    label,
    description,
    location,
    traffic = false,
    vehicles,
    users,
  }: {
    label?: string;
    description?: string;
    location?: Location | null;
    traffic?: boolean;
    vehicles: Vehicle[];
    users: string[];
  }): Promise<Project> {
    const response = await this.requests.post({
      uri: `/project`,
      data: {
        label,
        description,
        ...(location ? { location: LocationMapper.toDTO(location) } : {}),
        traffic,
        vehicles: vehicles.map(VehicleMapper.toRepository),
        users,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const project = response.data as ProjectDTO;

    return ProjectMapper.toDomain(project);
  }

  async delete({ project }: { project: Project }): Promise<void> {
    await this.requests.delete({
      uri: `/project/${project.id}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async fetch({ projectId }: { projectId: string }): Promise<Project> {
    const response = await this.requests.get({
      uri: `/project/${projectId}`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const project = response.data as ProjectDTO;

    return ProjectMapper.toDomain(project);
  }

  async fetchAll(): Promise<Array<Project>> {
    const response = await this.requests.get({
      uri: `/projects`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const projects = response.data as Array<ProjectDTO>;

    return projects.map(ProjectMapper.toDomain);
  }

  async fetchAllFlat(): Promise<Array<FlatProject>> {
    const response = await this.requests.get({
      uri: `/projects/flat`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const projects = response.data as Array<FlatProjectDTO>;

    return projects.map(FlatProjectMapper.toDomain);
  }

  async saveAsMonkey(project: Project): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}`,
      data: ProjectMapper.toRepositoryAsMonkey(project),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return ProjectMapper.toDomain(response.data);
  }

  async saveAsGorilla(project: Project): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}`,
      data: ProjectMapper.toRepositoryAsGorilla(project),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return ProjectMapper.toDomain(response.data);
  }

  async saveAsKong(project: Project): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}`,
      data: ProjectMapper.toRepositoryAsKong(project),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return ProjectMapper.toDomain(response.data);
  }

  async updateCommunicationEmails(project: Project): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}/emails`,
      data: ProjectCustomerEmailMapper.toRepository(project.customer_email),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
    return ProjectMapper.toDomain(response.data);
  }

  async updateCommunicationSMS(project: Project): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}/sms`,
      data: ProjectCustomerSMSMapper.toRepository(project.customer_sms),
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    return ProjectMapper.toDomain(response.data);
  }

  async sendTestCommunicationEmail({
    projectId,
    payload,
  }: {
    projectId: string;
    payload: {
      type: ProjectCustomerEmailTypes;
      email: string;
      from?: string;
      subject?: string;
      body?: string;
    };
  }): Promise<void> {
    await this.requests.post({
      uri: `/project/${projectId}/communication/test?channel=email`,
      data: {
        ...payload,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async fetchAvatar({ project }: { project: Project }): Promise<Project> {
    if (!project.avatar_url) {
      return project;
    }

    const requests = Requests.create(``);

    const response = await requests.get({
      uri: project.avatar_url,
      responseType: `arraybuffer`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    project.project_avatar = await arrayBufferToImageSrcBase64({
      data: response.data,
      dataType: response.headers[`content-type`].toLowerCase(),
    });

    return project;
  }

  async createAvatar({
    project,
    fileName,
    mimeType,
    file,
  }: {
    project: Project;
    fileName: string;
    mimeType: string;
    file: Blob;
  }): Promise<Project> {
    const formData = new FormData();
    formData.append(`name`, fileName);
    formData.append(`mime`, mimeType);
    formData.append(`data`, file);

    const response = await this.requests.post({
      uri: `/project/${project.id}/avatar`,
      data: formData,
      headers: {
        'content-type': `multipart/form-data`,
        ...this.sessionRepository.getAuthenticationHeader(),
      },
    });

    project.avatar_url = response.data.avatar_url;

    return project;
  }

  async deleteAvatar({ project }: { project: Project }): Promise<Project> {
    await this.requests.delete({
      uri: `/project/${project.id}/avatar`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    project.avatar_url = undefined;
    project.project_avatar = undefined;
    return project;
  }

  async createStyleImage({
    project,
    fileName,
    mimeType,
    imageType,
    file,
    domain,
  }: {
    project: Project;
    fileName: string;
    mimeType: string;
    imageType: `logo` | `banner` | `favicon`;
    file: Blob;
    domain: `customers` | `supervisors`;
  }): Promise<void> {
    const formData = new FormData();
    formData.append(`name`, fileName);
    formData.append(`mime`, mimeType);
    formData.append(`data`, file);

    await this.requests.post({
      uri: `/project/${project.id}/${domain}/style?image_type=${imageType}`,
      data: formData,
      headers: {
        'content-type': `multipart/form-data`,
        ...this.sessionRepository.getAuthenticationHeader(),
      },
    });
  }

  async deleteStyleImage({
    project,
    imageType,
    domain,
  }: {
    project: Project;
    imageType: `logo` | `banner` | `favicon`;
    domain: `customers` | `supervisors`;
  }): Promise<void> {
    await this.requests.delete({
      uri: `/project/${project.id}/${domain}/style?image_type=${imageType}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async retrieveUsers({ project }: { project: Project }): Promise<ProjectUser[]> {
    const response = await this.requests.get({
      uri: `/project/${project.id}/users`,
      params: {},
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const projectUsers = response.data as ProjectUserDTO[];

    return projectUsers.map((projectUser) => ProjectUserMapper.toDomain(projectUser));
  }

  async createUser({
    userId,
    role,
    project,
  }: {
    userId: string;
    role: ProjectUserRoles;
    project: Project;
  }): Promise<Project> {
    const response = await this.requests.post({
      uri: `/project/${project.id}/users`,
      data: {
        user_id: userId,
        role: role,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async deleteUser({ projectUser, project }: { projectUser: ProjectUser; project: Project }): Promise<Project> {
    const response = await this.requests.delete({
      uri: `/project/${project.id}/users/${projectUser.id}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async editUser({ projectUser, project }: { projectUser: ProjectUser; project: Project }): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${project.id}/users/${projectUser.id}`,
      data: {
        role: projectUser.role,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async createCustomField({
    customFieldDefinition,
  }: {
    customFieldDefinition: CustomFieldDefinition;
  }): Promise<Project> {
    const response = await this.requests.post({
      uri: `/project/${customFieldDefinition.project_id}/custom_fields?field_model=${customFieldDefinition.field_model}`,
      data: {
        label: customFieldDefinition.label,
        field_type: customFieldDefinition.field_type,
        custom_id: customFieldDefinition.custom_id,
        description: customFieldDefinition.description,
        enabled: customFieldDefinition.enabled,
        multiple: customFieldDefinition.multiple,
        options: customFieldDefinition.options,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async deleteCustomField({ customField }: { customField: CustomFieldDefinition }): Promise<Project> {
    const response = await this.requests.delete({
      uri: `/project/${customField.project_id}/custom_fields/${customField.custom_id}?field_model=${customField.field_model}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async updateCustomField({
    label,
    description,
    enabled,
    customField,
  }: {
    label?: string;
    description?: string;
    enabled?: boolean;
    customField: CustomFieldDefinition;
  }): Promise<Project> {
    const response = await this.requests.put({
      uri: `/project/${customField.project_id}/custom_fields/${customField.custom_id}?field_model=${customField.field_model}`,
      data: {
        ...(label ? { label } : {}),
        ...(description !== undefined ? { description } : {}),
        ...(enabled !== undefined ? { enabled } : {}),
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedProject = response.data as ProjectDTO;

    return ProjectMapper.toDomain(updatedProject);
  }

  async fetchProjectGeoFences({ projectId }: { projectId: String }): Promise<GeoFence[]> {
    const response = await this.requests.get({
      uri: `/v2/project/${projectId}/geo_fences`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const geoFences = response.data as GeoFenceDTO[];

    return geoFences.map(GeoFenceMapper.toDomain);
  }

  async fetchGeoFence({ geoFenceId }: { geoFenceId: string }): Promise<GeoFence> {
    const response = await this.requests.get({
      uri: `/v2/geo_fence/${geoFenceId}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const geoFence = response.data as GeoFenceDTO;

    return GeoFenceMapper.toDomain(geoFence);
  }

  async createGeoFence({ project, geoFence }: { project: Project; geoFence: GeoFence }): Promise<GeoFence> {
    const { label, enabled, color, coordinates } = GeoFenceMapper.toDTO(geoFence);

    const response = await this.requests.post({
      uri: `/v2/geo_fence?project_id=${project.id}`,
      data: {
        label,
        enabled,
        color,
        coordinates,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newGeoFence = response.data as GeoFenceDTO;

    return GeoFenceMapper.toDomain(newGeoFence);
  }

  async updateGeoFence({ geoFence }: { project: Project; geoFence: GeoFence }): Promise<GeoFence> {
    const { label, enabled, color, coordinates } = GeoFenceMapper.toDTO(geoFence);

    const response = await this.requests.put({
      uri: `/v2/geo_fence/${geoFence.id}`,
      data: {
        label,
        enabled,
        color,
        coordinates,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });

    const newGeoFence = response.data as GeoFenceDTO;

    return GeoFenceMapper.toDomain(newGeoFence);
  }

  async updateGeoFences({ geoFences }: { project: Project; geoFences: GeoFence[] }): Promise<Array<GeoFence | null>> {
    const requests = geoFences.map((geoFence) => {
      const { label, enabled, color, coordinates } = GeoFenceMapper.toDTO(geoFence);
      return {
        method: this.requests.put.bind(this.requests),
        params: {
          uri: `/v2/geo_fence/${geoFence.id}`,
          data: {
            label,
            enabled,
            color,
            coordinates,
          },
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    const response = await this.requests.multipleAsyncWait({
      requests,
    });

    const newGeoFences = response.flat(1) as GeoFenceDTO[];

    return newGeoFences.map((geoFence: GeoFenceDTO | null) => {
      return geoFence !== null ? GeoFenceMapper.toDomain(geoFence) : null;
    });
  }

  async deleteGeoFence({ geoFence }: { project: Project; geoFence: GeoFence }): Promise<void> {
    await this.requests.delete({
      uri: `/v2/geo_fence/${geoFence.id}`,
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async deleteGeoFences({ geoFences }: { project: Project; geoFences: GeoFence[] }): Promise<void> {
    const requests = geoFences.map((geoFence) => {
      return {
        method: this.requests.delete.bind(this.requests),
        params: {
          uri: `/v2/geo_fence/${geoFence.id}`,
          headers: this.sessionRepository.getAuthenticationHeader(),
        },
      };
    });

    await this.requests.multipleAsyncWait({
      requests,
    });
  }

  async updateProjectTraffic({ projectId, traffic }: { projectId: string; traffic: boolean }): Promise<void> {
    await this.requests.put({
      uri: `/project/${projectId}/traffic`,
      data: {
        traffic,
      },
      headers: this.sessionRepository.getAuthenticationHeader(),
    });
  }
}
